import { useRef } from "react";

import { isToday as getIsToday, isSameDay } from "@internationalized/date";
import { useCalendarCell, type AriaCalendarCellProps } from "react-aria";
import { type RangeCalendarState, type CalendarState } from "react-stately";

import { cn } from "#utils";

interface CalendarCellProps<
  T extends CalendarState | RangeCalendarState,
> extends AriaCalendarCellProps {
  state: T;
}

export function CalendarCell<T extends CalendarState | RangeCalendarState>({
  state,
  date,
}: CalendarCellProps<T>) {
  const ref = useRef(null);
  const { cellProps, buttonProps, isSelected, formattedDate, isDisabled } = useCalendarCell(
    { date },
    state,
    ref,
  );

  const isToday = getIsToday(date, state.timeZone);

  const isRangeState = "highlightedRange" in state;

  const isSelectionStart =
    isRangeState && state.highlightedRange
      ? isSameDay(date, state.highlightedRange.start)
      : isSelected;

  const isSelectionEnd =
    isRangeState && state.highlightedRange
      ? state.highlightedRange
        ? isSameDay(date, state.highlightedRange.end)
        : isSelected
      : false;

  const isRoundedLeft = isRangeState && isSelected && isSelectionStart;
  const isRoundedRight = isRangeState && isSelected && isSelectionEnd;

  return (
    <td {...cellProps}>
      <div
        {...buttonProps}
        className={cn(
          "relative flex cursor-pointer items-center justify-center rounded-md p-sm text-label-medium outline-none transition-all hover:bg-background-secondary",
          isRoundedLeft ? "rounded-none rounded-l-md" : "",
          isRoundedRight ? "rounded-none rounded-r-md" : "",
          isToday ? "text-text-brand hover:bg-transparent" : "",
          isSelected
            ? isRangeState && !isSelectionStart && !isSelectionEnd
              ? "rounded-none bg-interactive-secondary-button-focus-background hover:bg-brand-primary hover:text-text-primary-inverse"
              : "bg-background-brand text-text-primary-inverse hover:bg-brand-primary"
            : "",
          isDisabled ? "hover:bg-inherit text-text-disabled hover:text-text-disabled" : "",
        )}
        ref={ref}
      >
        {formattedDate}
        <div
          className={cn(
            "absolute top-7 size-[.3rem] rounded-full bg-brand-primary opacity-0 transition-opacity",
            isToday ? "opacity-100" : "",
          )}
        />
      </div>
    </td>
  );
}
